import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { purchaseRequests as purchaseRequestsApi, business, type Department } from '../../lib/api';
import { useAuth } from '../../lib/auth';

export default function PurchaseRequestSubmit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const needsDepartmentPicker = user?.role === 'director' || user?.role === 'ceo';

  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [estimatedAmount, setEstimatedAmount] = useState('');
  const [justification, setJustification] = useState('');
  const [neededByDate, setNeededByDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (needsDepartmentPicker) {
      business.departments.list().then(({ departments }) => setDepartments(departments)).catch(() => {});
    }
  }, [needsDepartmentPicker]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const parsedAmount = parseFloat(estimatedAmount);
    if (!parsedAmount || parsedAmount <= 0) { setError('Enter a valid positive amount'); return; }
    if (!itemDescription.trim()) { setError('Describe what you want to buy'); return; }
    if (needsDepartmentPicker && !departmentId) { setError('Choose a department'); return; }

    setLoading(true);
    try {
      const { purchaseRequest } = await purchaseRequestsApi.create({
        department_id: needsDepartmentPicker ? departmentId : undefined,
        item_description: itemDescription,
        estimated_amount: parsedAmount,
        justification: justification || undefined,
        needed_by_date: neededByDate || undefined,
      });
      navigate(`/purchase-requests/${purchaseRequest.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to submit purchase request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/purchase-requests/mine" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">New Purchase Request</h1>
      </div>

      <div className="card space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {needsDepartmentPicker && (
            <div>
              <label className="label">Department <span className="text-red-500">*</span></label>
              <select className="input-field" value={departmentId} onChange={e => setDepartmentId(e.target.value)} required>
                <option value="">Choose a department...</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">What do you want to buy? <span className="text-red-500">*</span></label>
            <input
              type="text" className="input-field" placeholder="e.g. New laptop for the design team"
              value={itemDescription} onChange={e => setItemDescription(e.target.value)} maxLength={200} required
            />
          </div>

          <div>
            <label className="label">Estimated amount <span className="text-red-500">*</span></label>
            <input
              type="number" step="0.01" min="0.01" className="input-field" placeholder="0.00"
              value={estimatedAmount} onChange={e => setEstimatedAmount(e.target.value)} required
            />
          </div>

          <div>
            <label className="label">Justification <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input-field resize-none" rows={3} placeholder="Why do you need this?"
              value={justification} onChange={e => setJustification(e.target.value)}
            />
          </div>

          <div>
            <label className="label">Needed by <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="date" className="input-field" value={neededByDate} onChange={e => setNeededByDate(e.target.value)} />
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/purchase-requests/mine" className="flex-1 btn-secondary text-center">Cancel</Link>
            <button type="submit" className="flex-1 btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
